import { createRef, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete, MdEdit, MdPreview } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import { apiUrlStatisticPages } from "~constants";
import DialogHelper from "~helpers/DialogHelper";
import { deleteStatisticPage } from "~store/statisticPages/actions";
import { selectCurrentProject, selectUserInfo } from "~store/user/selectors";

import VisibilitySettingsModal from "../StatisticPagesModals/VisibilitySettingsModal";

export default function StatisticPagesList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = createRef();

  const userInfo = useSelector(selectUserInfo);

  const currentProject = useSelector(selectCurrentProject);

  const columns = useMemo(() => {
    const handleClickDelete = (item) => async () => {
      if (!(await DialogHelper.showQuestionYesNo(t("common.delete"), t("dashboard.statisticPages.deleteQuestion"))))
        return;
      // TODO: Delete Statistic Page
      await dispatch(deleteStatisticPage(currentProject.id, item.id));

      tableRef.current?.refresh();
    };

    return [
      {
        label: t("common.id"),
        key: "id",
      },
      {
        label: t("common.name"),
        key: "name",
      },
      {
        label: t("dashboard.statisticPages.isDefault"),
        key: "is_default",
        render: (row) => (row.is_default ? t("common.yes") : t("common.no")),
      },
      userInfo?.is_superuser && {
        label: t("dashboard.statisticPages.isActive"),
        key: "is_active",
        render: (row) => (row.is_active ? t("common.yes") : t("common.no")),
      },
      {
        label: t("common.actions"),
        key: "actions",
        fixed: "right",
        actions: true,
        render: (row) => (
          <div className="d-flex justify-content-center gap-1">
            <PRButton
              outline
              color="primary"
              icon={MdPreview}
              link={`/stats/settings/statistic-pages/preview/${row.id}`}
              size="sm"
              tooltipText={t("dashboard.statisticPages.previewPageTooltip")}
            />
            <PRButton
              outline
              color="primary"
              icon={MdEdit}
              link={`/stats/settings/statistic-pages/form/${row.id}`}
              size="sm"
              tooltipText={t("dashboard.statisticPages.editPageTooltip")}
            />
            <PRButton
              outline
              color="danger"
              icon={MdDelete}
              size="sm"
              tooltipText={t("common.delete")}
              onClick={handleClickDelete(row)}
            />
          </div>
        ),
      },
    ];
  }, [tableRef, t, userInfo]);

  const actions = useMemo(
    () => [
      {
        label: t("common.create"),
        icon: MdAdd,
        color: "success",
        link: "/stats/settings/statistic-pages/form",
      },
      {
        label: t("dashboard.statisticPages.visibilitySettings"),
        icon: MdEdit,
        color: "primary",
        onClick: async () => {
          // TODO: Open visibility settings modal
          await VisibilitySettingsModal.show({});
        },
      },
    ],
    [t]
  );
  const parentName = [
    {
      label: t("dashboard.statisticPages"),
    },
  ];
  return (
    <PRContainer
      actions={actions}
      description={t("dashboard.statisticPages.description")}
      name={t("common.statistics")}
      parentName={parentName}
    >
      <div className="mt-2">
        <PRTable ref={tableRef} columns={columns} url={apiUrlStatisticPages.get.format(currentProject?.id)} />
      </div>
    </PRContainer>
  );
}
